import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";

export default function DatenschutzPage() {
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, []);

    return (
        <>
            <Navigation />

            <section
                data-testid="datenschutz-page"
                className="relative pt-32 lg:pt-40 pb-24 lg:pb-32 bg-[#FEFEFE]"
            >
                <div className="max-w-4xl mx-auto px-6 lg:px-12">
                    <Link
                        to="/"
                        data-testid="datenschutz-back-link"
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#14100D]/60 hover:text-[#E20514] transition-colors"
                    >
                        <ArrowLeft size={14} /> Zurück zur Startseite
                    </Link>

                    <div className="mt-10">
                        <div className="eyebrow mb-6">Datenschutz</div>
                        <h1
                            className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#14100D] leading-[1.02]"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                            Ihre Daten,
                            <br />
                            <span className="font-medium">respektvoll behandelt.</span>
                        </h1>
                        <p className="mt-6 text-[15px] text-[#14100D]/70 max-w-2xl">
                            Der Schutz Ihrer persönlichen Daten ist uns wichtig. In dieser Erklärung informieren wir Sie transparent über Art, Umfang und Zweck der Datenbearbeitung.
                        </p>
                    </div>

                    <div className="mt-16 space-y-12 text-[#14100D]">
                        <Section title="1. Verantwortliche Stelle">
                            <p>CLER BAU AG</p>
                            <p>Talstrasse 1, 8852 Altendorf</p>
                            <p>E-Mail: <a href="mailto:info@clerbau.ch" className="hover:text-[#E20514]">info@clerbau.ch</a></p>
                        </Section>

                        <Section title="2. Erhebung und Bearbeitung von Personendaten">
                            <p>
                                Wir bearbeiten Personendaten in Übereinstimmung mit dem revidierten Schweizer Datenschutzgesetz (revDSG) und – soweit anwendbar – der Europäischen Datenschutz-Grundverordnung (DSGVO).
                            </p>
                            <p className="mt-3">
                                Personendaten sind Angaben, die sich auf eine bestimmte oder bestimmbare natürliche Person beziehen. Dazu zählen unter anderem Name, Adresse, Telefonnummer, E-Mail-Adresse sowie Informationen, die Sie uns über das Kontaktformular übermitteln.
                            </p>
                        </Section>

                        <Section title="3. Zweck der Datenbearbeitung">
                            <p>Wir verwenden Ihre Daten ausschliesslich zu folgenden Zwecken:</p>
                            <ul className="list-disc pl-5 mt-3 space-y-1">
                                <li>Bearbeitung Ihrer Anfrage über das Kontaktformular</li>
                                <li>Erstellung von Offerten und Angeboten</li>
                                <li>Kommunikation im Rahmen eines Auftrags</li>
                                <li>Erfüllung gesetzlicher Aufbewahrungspflichten</li>
                            </ul>
                        </Section>

                        <Section title="4. Weitergabe an Dritte">
                            <p>
                                Eine Weitergabe Ihrer Daten an Dritte erfolgt nur, wenn dies zur Vertragserfüllung erforderlich ist, Sie eingewilligt haben oder eine gesetzliche Verpflichtung besteht. Zur Zustellung von Kontaktanfragen setzen wir den E-Mail-Dienst <span className="font-medium">Resend</span> ein.
                            </p>
                        </Section>

                        <Section title="5. Aufbewahrung und Löschung">
                            <p>
                                Wir bewahren Ihre Daten nur so lange auf, wie es für die genannten Zwecke oder zur Einhaltung gesetzlicher Pflichten erforderlich ist. Anschliessend werden die Daten gelöscht.
                            </p>
                        </Section>

                        <Section title="6. Ihre Rechte">
                            <p>Sie haben jederzeit das Recht auf:</p>
                            <ul className="list-disc pl-5 mt-3 space-y-1">
                                <li>Auskunft über die zu Ihrer Person bearbeiteten Daten</li>
                                <li>Berichtigung unrichtiger Daten</li>
                                <li>Löschung Ihrer Daten (soweit gesetzlich zulässig)</li>
                                <li>Einschränkung der Bearbeitung</li>
                                <li>Widerspruch gegen die Bearbeitung</li>
                            </ul>
                            <p className="mt-3">
                                Für eine Anfrage genügt eine formlose E-Mail an{" "}
                                <a href="mailto:info@clerbau.ch" className="hover:text-[#E20514]">info@clerbau.ch</a>.
                            </p>
                        </Section>

                        <Section title="7. Cookies und Analyse">
                            <p>
                                Diese Webseite verwendet ausschliesslich technisch notwendige Cookies. Es findet keine Nutzerprofilierung und keine kommerzielle Auswertung Ihres Verhaltens statt.
                            </p>
                        </Section>

                        <Section title="8. Datensicherheit">
                            <p>
                                Wir treffen angemessene technische und organisatorische Massnahmen, um Ihre Daten gegen Verlust, Missbrauch und unbefugten Zugriff zu schützen. Die Datenübertragung erfolgt verschlüsselt (SSL/TLS).
                            </p>
                        </Section>

                        <Section title="9. Anpassungen">
                            <p>
                                Wir behalten uns vor, diese Datenschutzerklärung jederzeit anzupassen. Die aktuelle Fassung ist auf dieser Seite abrufbar.
                            </p>
                        </Section>

                        <Section title="10. Anwendbares Recht">
                            <p>
                                Es gilt ausschliesslich schweizerisches Recht. Gerichtsstand ist Altendorf, Kanton Schwyz.
                            </p>
                        </Section>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
}

function Section({ title, children }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 md:gap-10 pb-8 border-b border-[rgba(20,16,13,0.1)]">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#E20514] font-medium leading-relaxed">
                {title}
            </div>
            <div className="text-[15px] leading-relaxed text-[#14100D]/85">
                {children}
            </div>
        </div>
    );
}
